import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ServiceTag {
  id: number;
  name: string;
  slug: string;
}

interface AgentCardProps {
  agent: {
    id: number;
    slug?: string;
    name: string;
    area: string;
    image: string | null;
    alt: string;
    rating: number;
    reviewCount: number;
    phone: string;
    completedOrders: number;
    experienceYears?: number;
    services?: ServiceTag[];
    availability?: string;
  };
}

const DEFAULT_AGENT_IMAGE = '/assets/PlaceHolder.png';

const AgentCard = ({ agent }: AgentCardProps) => {
  const whatsappMessage = encodeURIComponent(
    `Assalamualaikum ${agent.name}, saya ingin bertanya tentang servis dobi anda di ${agent.area}.`
  );
  const whatsappLink = `https://wa.me/${agent.phone}?text=${whatsappMessage}`;
  const profileUrl = agent.slug ? `/agent-profile/${agent.slug}` : `/agent-profile/${agent.id}`;

  const experienceYears = agent.experienceYears || 1;
  const ordersDisplay = agent.completedOrders >= 1000
    ? `${(agent.completedOrders / 1000).toFixed(1)}k+`
    : `${agent.completedOrders}+`;

  return (
    <div className="bg-card rounded-3xl border border-border overflow-hidden shadow-soft hover:shadow-brand hover:-translate-y-1 transition-all duration-300 flex flex-col">
      {/* Profile Image */}
      <div className="relative h-48 overflow-hidden bg-gradient-to-br from-primary/10 to-accent/10">
        <AppImage
          src={agent.image || DEFAULT_AGENT_IMAGE}
          alt={agent.alt}
          className="w-full h-full object-cover"
          fallbackSrc={DEFAULT_AGENT_IMAGE}
        />
        {agent.availability === 'Sedia Menerima Tempahan' && (
          <div className="absolute top-4 left-4 bg-success text-white text-[10px] font-black px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
            </span>
            SEDIA MENERIMA TEMPAHAN
          </div>
        )}
        {agent.availability && agent.availability !== 'Sedia Menerima Tempahan' && (
          <div className="absolute top-4 left-4 bg-amber-500 text-white text-[10px] font-black px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
            <Icon name="ExclamationCircleIcon" size={12} variant="solid" />
            SLOT PENUH
          </div>
        )}
        {agent.rating > 0 && (
          <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm text-textPrimary text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 shadow-lg">
            <Icon name="StarIcon" size={14} variant="solid" className="text-yellow-500" />
            <span>{agent.rating}/5</span>
            <span className="text-textSecondary font-normal">({agent.reviewCount})</span>
          </div>
        )}
      </div>

      <div className="p-5 flex-1 flex flex-col">
        {/* Name */}
        <h3 className="text-xl font-heading font-bold text-textPrimary mb-1">{agent.name}</h3>

        {/* Location */}
        <div className="flex items-center text-sm text-textSecondary mb-4">
          <Icon name="MapPinIcon" size={16} variant="solid" className="mr-1 text-primary" />
          {agent.area}
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="border border-border rounded-2xl p-3 text-center">
            <div className="text-xl font-heading font-bold text-accent">{experienceYears} Tahun</div>
            <div className="text-[10px] font-semibold text-textTertiary uppercase tracking-wider">Pengalaman</div>
          </div>
          <div className="border border-border rounded-2xl p-3 text-center">
            <div className="text-xl font-heading font-bold text-accent">{ordersDisplay}</div>
            <div className="text-[10px] font-semibold text-textTertiary uppercase tracking-wider">Pesanan</div>
          </div>
        </div>

        {/* Service Tags */}
        {agent.services && agent.services.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {agent.services.map((service) => (
              <span
                key={service.id}
                className="px-3 py-1.5 bg-muted text-textSecondary text-[10px] font-bold uppercase tracking-wider rounded-full border border-border"
              >
                {service.name}
              </span>
            ))}
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3 mt-auto">
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 bg-whatsapp text-white py-3 rounded-full font-cta font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={18} variant="outline" />
            WhatsApp
          </a>
          <Link
            href={profileUrl}
            className="flex-1 bg-primary text-white py-3 rounded-full font-cta font-bold hover:bg-brandDark transition-colors flex items-center justify-center"
          >
            Profil
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AgentCard;
